// Inspired by https://github.com/vercel/next.js/blob/canary/examples/with-apollo/lib/apolloClient.js
/*
*SSR request*
1. getServerSideProps runs, calling queryServerSide(..., query, ...)
- queryServerSide calls sharedServerProps
-- sharedServerProps calls initApollo(null, ..), which creates an empty apollo client
- queryServerSide calls client.query() to warm the cache for the query's data
- queryServerSide then pulls the state from the client and adds it to the SSR props via addApolloState()

2. Then (still on the server), the component renders
- The component is wrapped in withServerProps, which calls useApollo with the pageProps that was passed to the component
-- useApollo pulls the apollo state from the props and passes them into initApollo(state, ...)
--- initApollo creates a new apollo client instance (always new because we don't cache the client on the server) and initialise it with the state from the getServerSideProps
- Now when the compoent calls useQuery(query) it runs against a warm apollo client and thus has data right away.

3. Finally, the component renders again on the client, with the same props
- The same stack of things runs as on the server component render, with one difference, the apolloClient instance is saved for the next query (see below)

*Client-rendered query*
1. getServerSideProps runs as above, returning the cached query results in props over the wire.

2. Now the component renders on the client, as above, but the cache already exists with the results from the previous query inside.
- Again withServerProps pulls the new query's apollo state from the props and passes it through to initApollo
-- Now initApollo(newState, ....) ends up merging the state from the second query into the existing cache.
*/
import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import fetch from 'isomorphic-fetch';
import { keyBy, values } from 'lodash';
import { NextPageContext } from 'next';

import possibleTypes from '../chromatic-lib/schema/fragmentTypes.json';
import { concatUniquePagination, mergeNumericValues } from './apolloCacheUtils';
import { isMustLoginError, logout } from './authentication';
import { seenOperations } from './graphQLOperationMemory';
import { mergeTestsGroupedByComponent } from './mergeTestsGroupedByComponent';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';
export const SESSION_COOKIE_NAME = 'x-chromatic-session-id';

export type InitialState = {
  apollo?: {
    data: Parameters<InstanceType<typeof InMemoryCache>['restore']>[0];
  };
};

export type HttpOptions = Parameters<typeof createHttpLink>[0];

export type CacheOptions = ConstructorParameters<typeof InMemoryCache>[0];

export const chromaticCacheOptions: CacheOptions = {
  possibleTypes,
  typePolicies: {
    App: {
      fields: {
        collaborators: {
          keyArgs: false,
          merge(existing, incoming) {
            return {
              ...existing,
              ...incoming,
              nodes: values(
                keyBy(
                  [...((existing || {}).nodes || []), ...((incoming || {}).nodes || [])],
                  '__ref'
                )
              ),
            };
          },
        },
      },
    },
    Build: {
      fields: {
        testsGroupedByComponent: {
          keyArgs: ['testSearchString'],
          merge: mergeTestsGroupedByComponent,
        },
        testsGroupedByViolation: {
          keyArgs: ['violations'],
          merge: concatUniquePagination,
        },
      },
    },
    TestsGroupedByViolations: {
      keyFields: ['__typename', 'id', 'buildId'],
      fields: {
        components: {
          keyArgs: false,
          merge: concatUniquePagination,
        },
      },
    },
    ComponentGroup: {
      keyFields: ['__typename', 'id', 'buildId', 'rule'],
      fields: {
        accessibilityTotalViolationNodeCount: {
          merge: mergeNumericValues,
        },
        specs: {
          merge: concatUniquePagination,
        },
      },
    },
    SpecGroup: {
      keyFields: ['__typename', 'id', 'buildId', 'rule'],
    },
    TestForSpecGroup: {
      keyFields: ['__typename', 'id', 'buildId', 'rule'],
    },
  },
};

export function createApolloClient(
  initialState: InitialState,
  httpOptions: HttpOptions,
  cacheOptions: CacheOptions
) {
  const cache = new InMemoryCache(cacheOptions);
  return new ApolloClient({
    name: 'webapp',
    cache: cache.restore(initialState.apollo ? initialState.apollo.data : {}),
    ssrMode: typeof window === 'undefined', // Disables forceFetch on the server (so queries are only run once)
    connectToDevTools: true,
    link: ApolloLink.from([
      onError(({ graphQLErrors }) => {
        // do nothing on the server side
        if (typeof window === 'undefined') {
          return;
        }

        if (graphQLErrors?.some((error) => isMustLoginError(error))) {
          /*
           * logging out will rerender pages with isLoggedIn = false as EnsureLoggedIn (services/webapp/lib/authenticated.jsx) is an observer
           * of the observable tokenSingleton (services/webapp/lib/authentication.ts). EnsureLoggedIn is rendered by both pages composed
           * with `page` and `unauthenticatedPage`. This rerender will cause the user to be booted to the login screen where necessary.
           * - for pages composed with `page`, this occurs in EnsureLoggedIn (services/webapp/lib/authenticated.jsx)
           * - for pages composed with `unauthenticatedPage`, this should occur via ErrorScreen (services/webapp/screens/Error/ErrorScreen.tsx)
           *   rendering LoginScreen when detecting a "Must login" error.
           */
          logout();
        }
      }),
      createHttpLink({
        fetch,
        credentials: 'same-origin',
        ...httpOptions,
        uri: (operation) =>
          `${httpOptions.uri}?operationName=${encodeURIComponent(operation.operationName)}`,
      }),
    ]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        // nextFetchPolicy below fixes a situation where Apollo considered any
        // change to our cache to be something that needed to be refetched.
        // This played out during refetchQueries -- when a refetch happened, a
        // page query would fire every time after to simulate the "and-network"
        // part of our default fetch policy.
        // https://github.com/apollographql/apollo-client/issues/6760#issuecomment-668188727
        nextFetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
    },
  });
}

let apolloClient: ApolloClient<NormalizedCacheObject> = null;

export default function initApollo(initialState: InitialState, httpOptions: HttpOptions) {
  seenOperations.clear();

  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!process.browser) {
    return createApolloClient(initialState, httpOptions, chromaticCacheOptions);
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = createApolloClient(initialState, httpOptions, chromaticCacheOptions);
  }

  return apolloClient;
}

export const getServerHttpOptions = ({ req }: NextPageContext, uri: string, sessionId: string) => {
  const incomingRequestId = req.headers['x-amzn-trace-id'] ?? req.headers['x-request-id'];
  const requestId =
    incomingRequestId && Array.isArray(incomingRequestId)
      ? incomingRequestId[0]
      : incomingRequestId || '';

  return {
    uri,
    headers: {
      cookie: req.headers.cookie,
      'x-request-id': requestId as string,
      'x-chromatic-session-id': sessionId,
    },
  };
};

export const getGraphCmsHttpOptions = () => ({
  uri: 'https://api-us-east-1.graphcms.com/v2/ckmcmdhph9v8c01w5cz6v4nq3/master',
  headers: {},
});
